/**
 * Custom hooks per Document Training
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiDeleteWithBody, apiGet, apiPatch, apiPost, apiPostFormData, apiPut } from "@/lib/api-client";
import { storage } from "@/lib/local-storage";
import type {
    DocumentLabTrainingCreate,
    DocumentLabTrainingListFilters,
    DocumentLabTrainingResponse,
    DocumentLabTrainingUpdate,
    ExtractionStrategyResponse,
    ExtractionResultResponse,
    SelectExtractionResponse,
    JobHistoryItem,
    JobHistoryResponse,
    FieldValueResponse,
    FieldValueUpdate,
    FieldValidationRequest,
    FieldValidationResponse,
    FieldHistoryItem,
    BatchParsingRequest,
    BatchParsingResponse,
    BatchClassificationRequest,
    BatchClassificationResponse,
    BatchReassignTypeRequest,
    BatchReassignTypeResponse,
    BatchDeleteResponse,
    ClassificationResultResponse,
    ChildTrainingDocumentResponse,
    SplitTrainingDocumentRequest,
    SplitTrainingDocumentResponse,
    GroupedFieldsResponse,
    GroundTruthRequest,
    GroundTruthResponse,
    GroundTruthDraftRequest,
} from "@/types/api/document-training";

const TRAINING_DOCUMENTS_QUERY_KEYS = {
    all: ["training-documents"] as const,
    lists: () => [...TRAINING_DOCUMENTS_QUERY_KEYS.all, "list"] as const,
    list: (filters?: DocumentLabTrainingListFilters) => [...TRAINING_DOCUMENTS_QUERY_KEYS.lists(), filters] as const,
    details: () => [...TRAINING_DOCUMENTS_QUERY_KEYS.all, "detail"] as const,
    detail: (id: string) => [...TRAINING_DOCUMENTS_QUERY_KEYS.details(), id] as const,
    downloads: () => [...TRAINING_DOCUMENTS_QUERY_KEYS.all, "download"] as const,
    download: (id: string) => [...TRAINING_DOCUMENTS_QUERY_KEYS.downloads(), id] as const,
    previews: () => [...TRAINING_DOCUMENTS_QUERY_KEYS.all, "preview"] as const,
    preview: (id: string) => [...TRAINING_DOCUMENTS_QUERY_KEYS.previews(), id] as const,
    extractions: (id: string) => [...TRAINING_DOCUMENTS_QUERY_KEYS.detail(id), "extractions"] as const,
};

const EXTRACTION_STRATEGIES_QUERY_KEYS = {
    all: ["extraction-strategies"] as const,
};

/**
 * Hook per lista training documents con filtri
 */
export function useTrainingDocuments(
    filters?: DocumentLabTrainingListFilters,
    options?: Omit<import("@tanstack/react-query").UseQueryOptions<DocumentLabTrainingResponse[], Error>, "queryKey" | "queryFn">,
) {
    return useQuery({
        queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.list(filters),
        queryFn: async (): Promise<DocumentLabTrainingResponse[]> => {
            return await apiGet<DocumentLabTrainingResponse[]>("/training/documents", {
                params: filters as Record<string, string | number | boolean | null | undefined> | undefined,
            });
        },
        ...options,
    });
}

/**
 * Hook per singolo training document
 */
export function useTrainingDocument(docId: string) {
    return useQuery({
        queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.detail(docId),
        queryFn: async (): Promise<DocumentLabTrainingResponse> => {
            return await apiGet<DocumentLabTrainingResponse>(`/training/documents/${docId}`);
        },
        enabled: !!docId,
    });
}

/**
 * Hook per creare training document con upload file
 */
export function useCreateTrainingDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ file, data }: { file: File; data: Omit<DocumentLabTrainingCreate, "file_path"> }): Promise<DocumentLabTrainingResponse> => {
            const formData = new FormData();
            formData.append("file", file);
            // Always send dataset_name (required field)
            formData.append("dataset_name", data.dataset_name || "default");
            // Send optional fields only when they have values
            if (data.dataset_version) {
                formData.append("dataset_version", data.dataset_version);
            }
            // Backend requires either document_type or document_type_id
            if (data.document_type) {
                formData.append("document_type", data.document_type);
            }
            if (data.document_type_id) {
                formData.append("document_type_id", data.document_type_id);
            }
            if (data.notes) {
                formData.append("notes", data.notes);
            }
            if (data.source_url) {
                formData.append("source_url", data.source_url);
            }
            if (data.attribute_metadata) {
                formData.append("attribute_metadata", JSON.stringify(data.attribute_metadata));
                // Send schema_id as top-level Form param for GCS path resolution
                if (data.attribute_metadata.document_schema_id) {
                    formData.append("schema_id", String(data.attribute_metadata.document_schema_id));
                }
            }
            return await apiPostFormData<DocumentLabTrainingResponse>("/training/documents", formData);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per aggiornare training document
 */
export function useUpdateTrainingDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ docId, data }: { docId: string; data: DocumentLabTrainingUpdate }): Promise<DocumentLabTrainingResponse> => {
            return await apiPut<DocumentLabTrainingResponse>(`/training/documents/${docId}`, data);
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.detail(variables.docId) });
        },
    });
}

/**
 * Hook per eliminare training document
 */
export function useDeleteTrainingDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (docId: string): Promise<void> => {
            return await apiDelete<void>(`/training/documents/${docId}`);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per ottenere blob URL del training document per il PDF viewer
 * Scarica il file tramite API autenticata e crea un blob URL
 */
export function useTrainingDocumentDownloadUrl(docId: string | undefined) {
    const enabled = !!docId && docId !== "";

    return useQuery({
        queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.download(docId || ""),
        queryFn: async (): Promise<string> => {
            if (!docId || docId === "") {
                throw new Error("Document ID is required");
            }

            // Costruisci URL per endpoint di download che fa da proxy a GCS
            const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
            const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
            const cleanBaseUrl = baseUrl.replace(/\/$/, "");
            const cleanVersionPrefix = versionPrefix.startsWith("/") ? versionPrefix : `/${versionPrefix}`;
            const downloadUrl = `${cleanBaseUrl}${cleanVersionPrefix}/training/documents/${docId}/download`;

            // Ottieni token di autenticazione usando il sistema di storage con prefisso
            const token = storage.getItem("access_token");
            if (!token) {
                throw new Error("Authentication token not found");
            }

            // Scarica il file tramite fetch con autenticazione
            const response = await fetch(downloadUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    // Token potrebbe essere scaduto, prova a fare refresh
                    throw new Error("Authentication failed. Please refresh the page.");
                }
                const errorText = await response.text().catch(() => response.statusText);
                throw new Error(`Failed to download document: ${response.status} ${response.statusText} - ${errorText}`);
            }

            // Crea blob dal contenuto della risposta
            const blob = await response.blob();

            // Crea blob URL
            const blobUrl = URL.createObjectURL(blob);

            return blobUrl;
        },
        enabled,
        staleTime: 0, // Download URLs sono sempre fresh
        // Cleanup del blob URL quando la query viene invalidata o il componente smontato
        gcTime: 0, // Non mantenere in cache per evitare memory leak
    });
}

/**
 * Hook per ottenere blob URL del training document per preview
 * Per file Excel (xlsx, xls), restituisce un'immagine convertita
 * Per PDF e immagini, restituisce il file originale
 */
export function useTrainingDocumentPreviewUrl(docId: string | undefined) {
    const enabled = !!docId && docId !== "";

    return useQuery({
        queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.preview(docId || ""),
        queryFn: async (): Promise<string> => {
            if (!docId || docId === "") {
                throw new Error("Document ID is required");
            }

            // Costruisci URL per endpoint di preview
            const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
            const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
            const cleanBaseUrl = baseUrl.replace(/\/$/, "");
            const cleanVersionPrefix = versionPrefix.startsWith("/") ? versionPrefix : `/${versionPrefix}`;
            const previewUrl = `${cleanBaseUrl}${cleanVersionPrefix}/training/documents/${docId}/preview`;

            // Ottieni token di autenticazione
            const token = storage.getItem("access_token");
            if (!token) {
                throw new Error("Authentication token not found");
            }

            // Scarica il file tramite fetch con autenticazione
            const response = await fetch(previewUrl, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error("Authentication failed. Please refresh the page.");
                }
                const errorText = await response.text().catch(() => response.statusText);
                throw new Error(`Failed to get document preview: ${response.status} ${response.statusText} - ${errorText}`);
            }

            // Crea blob dal contenuto della risposta
            const blob = await response.blob();

            // Crea blob URL
            const blobUrl = URL.createObjectURL(blob);

            return blobUrl;
        },
        enabled,
        staleTime: 0,
        gcTime: 0,
    });
}

/**
 * Hook per lista extraction strategies (API 3)
 */
export function useExtractionStrategies() {
    return useQuery({
        queryKey: EXTRACTION_STRATEGIES_QUERY_KEYS.all,
        queryFn: async (): Promise<ExtractionStrategyResponse[]> => {
            return await apiGet<ExtractionStrategyResponse[]>("/extraction-strategies");
        },
        staleTime: 30 * 60 * 1000, // 30 minuti - le strategie cambiano raramente
    });
}

/**
 * Hook per avviare estrazione su training document (API 7)
 */
export function useRunExtraction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            trainingDocId,
            strategy,
            schemaId,
        }: {
            trainingDocId: string;
            strategy?: string;
            schemaId?: string;
        }): Promise<ExtractionResultResponse> => {
            const params: Record<string, string> = {};
            if (strategy) params.strategy = strategy;
            if (schemaId) params.schema_id = schemaId;

            return await apiPost<ExtractionResultResponse>(
                `/training/documents/${trainingDocId}/extract`,
                {},
                { params }
            );
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.extractions(variables.trainingDocId),
            });
            queryClient.invalidateQueries({
                queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.detail(variables.trainingDocId),
            });
        },
    });
}

/**
 * Hook per lista estrazioni di un training document (API 8)
 */
export function useTrainingDocumentExtractions(trainingDocId: string | undefined) {
    const enabled = !!trainingDocId && trainingDocId !== "";

    return useQuery({
        queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.extractions(trainingDocId || ""),
        queryFn: async (): Promise<ExtractionResultResponse[]> => {
            if (!trainingDocId) {
                throw new Error("Training document ID is required");
            }
            return await apiGet<ExtractionResultResponse[]>(
                `/training/documents/${trainingDocId}/extractions`
            );
        },
        enabled,
    });
}

/**
 * Hook per selezionare miglior estrazione (API 9)
 */
export function useSelectExtraction() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            trainingDocId,
            parsingId,
        }: {
            trainingDocId: string;
            parsingId: string;
        }): Promise<SelectExtractionResponse> => {
            return await apiPost<SelectExtractionResponse>(
                `/training/documents/${trainingDocId}/extractions/${parsingId}/select`,
                {}
            );
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.extractions(variables.trainingDocId),
            });
        },
    });
}

/**
 * Hook per job history di un training document
 */
export function useTrainingDocumentJobs(trainingDocId: string | undefined) {
    const enabled = !!trainingDocId && trainingDocId !== "";

    return useQuery({
        queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.detail(trainingDocId || ""), "jobs"] as const,
        queryFn: async (): Promise<JobHistoryItem[]> => {
            if (!trainingDocId) {
                throw new Error("Training document ID is required");
            }
            // API returns { training_document_id, jobs: [...], summary: {...} }
            const response = await apiGet<JobHistoryResponse>(
                `/training/documents/${trainingDocId}/jobs`
            );
            return response.jobs || [];
        },
        enabled,
    });
}

/**
 * Hook per lista campi di un'estrazione
 */
export function useExtractionFields(trainingDocId: string | undefined, parsingId: string | undefined) {
    const enabled = !!trainingDocId && !!parsingId && trainingDocId !== "" && parsingId !== "";

    return useQuery({
        queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.extractions(trainingDocId || ""), parsingId, "fields"] as const,
        queryFn: async (): Promise<FieldValueResponse[]> => {
            if (!trainingDocId || !parsingId) {
                throw new Error("Training document ID and parsing ID are required");
            }
            return await apiGet<FieldValueResponse[]>(
                `/training/documents/${trainingDocId}/extractions/${parsingId}/fields`
            );
        },
        enabled,
    });
}

/**
 * Hook per aggiornare un campo estratto
 */
export function useUpdateExtractionField() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            trainingDocId,
            parsingId,
            fieldValueId,
            data,
        }: {
            trainingDocId: string;
            parsingId: string;
            fieldValueId: string;
            data: FieldValueUpdate;
        }): Promise<FieldValueResponse> => {
            return await apiPatch<FieldValueResponse>(
                `/training/documents/${trainingDocId}/extractions/${parsingId}/fields/${fieldValueId}`,
                data
            );
        },
        onSuccess: (_data, variables) => {
            // Invalidate both fields and grouped-fields queries
            queryClient.invalidateQueries({
                queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.extractions(variables.trainingDocId), variables.parsingId, "fields"],
            });
            queryClient.invalidateQueries({
                queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.extractions(variables.trainingDocId), variables.parsingId, "grouped-fields"],
            });
        },
    });
}

/**
 * Hook per validare un campo estratto (correct/incorrect)
 */
export function useValidateExtractionField() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            trainingDocId,
            parsingId,
            fieldValueId,
            data,
        }: {
            trainingDocId: string;
            parsingId: string;
            fieldValueId: string;
            data: FieldValidationRequest;
        }): Promise<FieldValidationResponse> => {
            return await apiPost<FieldValidationResponse>(
                `/training/documents/${trainingDocId}/extractions/${parsingId}/fields/${fieldValueId}/validate`,
                data
            );
        },
        onSuccess: (_data, variables) => {
            // Invalidate both fields and grouped-fields queries
            queryClient.invalidateQueries({
                queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.extractions(variables.trainingDocId), variables.parsingId, "fields"],
            });
            queryClient.invalidateQueries({
                queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.extractions(variables.trainingDocId), variables.parsingId, "grouped-fields"],
            });
        },
    });
}

/**
 * Hook per storia modifiche di un campo
 */
export function useFieldHistory(trainingDocId: string | undefined, parsingId: string | undefined, fieldValueId: string | undefined) {
    const enabled = !!trainingDocId && !!parsingId && !!fieldValueId;

    return useQuery({
        queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.extractions(trainingDocId || ""), parsingId, "fields", fieldValueId, "history"] as const,
        queryFn: async (): Promise<FieldHistoryItem[]> => {
            if (!trainingDocId || !parsingId || !fieldValueId) {
                throw new Error("All IDs are required");
            }
            return await apiGet<FieldHistoryItem[]>(
                `/training/documents/${trainingDocId}/extractions/${parsingId}/fields/${fieldValueId}/history`
            );
        },
        enabled,
    });
}

/**
 * Hook per classificare un singolo training document
 */
export function useClassifyTrainingDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (trainingDocId: string): Promise<ClassificationResultResponse> => {
            return await apiPost<ClassificationResultResponse>(
                `/training/documents/${trainingDocId}/classify`,
                {}
            );
        },
        onSuccess: (_data, trainingDocId) => {
            queryClient.invalidateQueries({
                queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.detail(trainingDocId),
            });
            queryClient.invalidateQueries({
                queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.detail(trainingDocId), "jobs"],
            });
        },
    });
}

/**
 * Hook per batch parsing
 */
export function useBatchParsing() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: BatchParsingRequest): Promise<BatchParsingResponse> => {
            return await apiPost<BatchParsingResponse>(
                "/training/documents/batch-parse",
                data
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per batch classification
 */
export function useBatchClassification() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: BatchClassificationRequest): Promise<BatchClassificationResponse> => {
            return await apiPost<BatchClassificationResponse>(
                "/training/documents/batch-classify",
                data
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per batch reassign document type
 */
export function useBatchReassignType() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: BatchReassignTypeRequest): Promise<BatchReassignTypeResponse> => {
            return await apiPost<BatchReassignTypeResponse>(
                "/training/documents/batch-reassign-type",
                data
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per batch delete
 */
export function useDeleteTrainingDocumentsBatch() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (trainingDocumentIds: string[]): Promise<BatchDeleteResponse> => {
            return await apiDeleteWithBody<BatchDeleteResponse>(
                "/training/documents/batch",
                { training_document_ids: trainingDocumentIds }
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per ottenere i documenti figli (split) di un training document
 */
export function useTrainingDocumentChildren(docId: string | undefined) {
    const enabled = !!docId && docId !== "";

    return useQuery({
        queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.detail(docId || ""), "children"] as const,
        queryFn: async (): Promise<ChildTrainingDocumentResponse[]> => {
            if (!docId) {
                throw new Error("Document ID is required");
            }
            return await apiGet<ChildTrainingDocumentResponse[]>(
                `/training/documents/${docId}/children`
            );
        },
        enabled,
    });
}

/**
 * Hook per splittare un training document multi-documento
 */
export function useSplitTrainingDocument() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            docId,
            pageRanges,
        }: {
            docId: string;
            pageRanges: SplitTrainingDocumentRequest["page_ranges"];
        }): Promise<SplitTrainingDocumentResponse> => {
            return await apiPost<SplitTrainingDocumentResponse>(
                `/training/documents/${docId}/split`,
                { page_ranges: pageRanges }
            );
        },
        onSuccess: (_data, variables) => {
            // Invalida la lista per mostrare i nuovi documenti figli
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
            // Invalida il documento padre per aggiornare split_count
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.detail(variables.docId) });
            // Invalida la lista children
            queryClient.invalidateQueries({
                queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.detail(variables.docId), "children"]
            });
        },
    });
}

/**
 * Hook per lista campi raggruppati di un'estrazione (collapsible groups)
 * Returns fields organized in collapsible groups with collapsed_preview_json for UI display
 */
export function useGroupedExtractionFields(trainingDocId: string | undefined, parsingId: string | undefined) {
    const enabled = !!trainingDocId && !!parsingId && trainingDocId !== "" && parsingId !== "";

    return useQuery({
        queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.extractions(trainingDocId || ""), parsingId, "grouped-fields"] as const,
        queryFn: async (): Promise<GroupedFieldsResponse> => {
            if (!trainingDocId || !parsingId) {
                throw new Error("Training document ID and parsing ID are required");
            }
            return await apiGet<GroupedFieldsResponse>(
                `/training/documents/${trainingDocId}/extractions/${parsingId}/grouped-fields`
            );
        },
        enabled,
    });
}

/**
 * Hook per ottenere ground truth di un training document
 */
export function useGroundTruth(docId: string | undefined) {
    const enabled = !!docId && docId !== "";

    return useQuery({
        queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.detail(docId || ""), "ground-truth"] as const,
        queryFn: async (): Promise<GroundTruthResponse> => {
            if (!docId) {
                throw new Error("Document ID is required");
            }
            return await apiGet<GroundTruthResponse>(
                `/training/documents/${docId}/ground-truth`
            );
        },
        enabled,
        retry: false, // Don't retry 404s (no ground truth yet)
    });
}

/**
 * Hook per salvare ground truth di un training document
 */
export function useSaveGroundTruth() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            docId,
            data,
        }: {
            docId: string;
            data: GroundTruthRequest;
        }): Promise<GroundTruthResponse> => {
            return await apiPost<GroundTruthResponse>(
                `/training/documents/${docId}/ground-truth`,
                data
            );
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: [...TRAINING_DOCUMENTS_QUERY_KEYS.detail(variables.docId), "ground-truth"],
            });
            queryClient.invalidateQueries({
                queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.detail(variables.docId),
            });
        },
    });
}

/**
 * Hook per scan GCS e creare training documents mancanti nel DB
 */
export function useScanGCS() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params?: { document_types?: string[]; dataset_name?: string; dry_run?: boolean }) => {
            const res = await apiPost<{
                total_files_found: number;
                already_in_db: number;
                newly_created: number;
                skipped: number;
                errors: string[];
            }>("/training/documents/scan-gcs", {
                dry_run: false,
                ...params,
            });
            return res;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: TRAINING_DOCUMENTS_QUERY_KEYS.lists() });
        },
    });
}

/**
 * Hook per auto-salvare la bozza di ground truth (no GCS, solo DB)
 */
export function useSaveGroundTruthDraft() {
    return useMutation({
        mutationFn: async ({
            docId,
            data,
        }: {
            docId: string;
            data: GroundTruthDraftRequest;
        }): Promise<{ status: string; saved_at: string }> => {
            return await apiPatch<{ status: string; saved_at: string }>(
                `/training/documents/${docId}/ground-truth/draft`,
                data
            );
        },
        // Silent save — no query invalidation to avoid re-renders
    });
}
